import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../store';
import { useLightMode } from '../utils';
import { AdminService } from '../api/AdminAPI';
import { NotifyService } from '../api/NotifyAPI';
import { AddStaffForm } from '../components/AddStaffForm/AddStaffForm';
import { ResetPasswordForm } from '../components/ResetPasswordForm/ResetPasswordForm';
import { NotifySendModal } from '../components/NotifyModal/NotifySendModal';
import { NotifyTable } from '../components/NotifyTable/NotifyTable';
import { AdminTable } from '../components/AdminTable/AdminTable';
import s from '../styles/staff.module.css';
import logoLight from '../assets/images/icon-only-black.png';
import logoDark from '../assets/images/icon-only-bronze.png';

export function AdminTablePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [lightMode, toggleLightMode] = useLightMode();
  const isAdmin = user?.role === 'admin';

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['staff-accounts'],
    queryFn:  () => AdminService.list(),
  });

  const staff = data?.staff || [];

  const handleCreate = useCallback(async (form) => {
    try {
      await AdminService.create(form);
      await refetch();
      return { status: 'success' };
    } catch (err) {
      return { status: 'error', message: err?.message || 'Failed to add staff' };
    }
  }, [refetch]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [notifyTarget, setNotifyTarget] = useState(null);
  const [resetTarget, setResetTarget]   = useState(null);
  const [showAddStaff, setShowAddStaff] = useState(false);

  const handleNotify = (staffMember) => () => setNotifyTarget(staffMember);
  const handleResetPassword = (staffMember) => setResetTarget(staffMember);

  const handleReset = useCallback(async (password, currentPassword) => {
    if (!resetTarget) return { status: 'error', message: 'No account selected' };
    try {
      await AdminService.resetPassword(resetTarget.idUser, password, currentPassword);
      return { status: 'success' };
    } catch (err) {
      return { status: 'error', message: err?.message || 'Failed to reset password' };
    }
  }, [resetTarget]);

  const handleSendNotify = useCallback(async ({ receiver, message, colors }) => {
    await NotifyService.send({ receiver, message, colors });
  }, []);

  return (
    <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
      <header className={s.header}>
        <span className={s.logo}>
          <img className={s.logoImg} src={lightMode ? logoLight : logoDark} alt="ACE CAR SPA" />
        </span>
        <div className={s.headerActions}>
          {isAdmin && (
            <button onClick={() => setShowAddStaff(true)} className={s.primaryBtn}>Add Admin</button>
          )}
          <Link to="/dashboard" className={s.linkBtn}>← Dashboard</Link>
          <button onClick={() => toggleLightMode(l => !l)} className={s.linkBtn}>
            {lightMode ? 'Dark Mode' : 'Light Mode'}
          </button>
          <button onClick={handleLogout} className={s.linkBtn}>Logout</button>
        </div>
      </header>

      <div className={s.body} style={{ gridTemplateColumns: '1fr' }}>
        <div>
          <AdminTable
            users={staff}
            isLoading={isLoading}
            isAdmin={isAdmin}
            currentUserId={user?.id}
            onNotify={handleNotify}
            onResetPassword={handleResetPassword}
          />
          {isAdmin ? <NotifyTable /> : null}
        </div>
      </div>

      {notifyTarget ? (
        <NotifySendModal
          receiver={notifyTarget}
          onClose={() => setNotifyTarget(null)}
          onSend={handleSendNotify}
          light={lightMode}
        />
      ) : null}

      {resetTarget ? (
        <div className={s.modalOverlay} onClick={() => setResetTarget(null)}>
          <div className={s.modalBody} onClick={(e) => e.stopPropagation()}>
            <ResetPasswordForm
              target={resetTarget}
              isSelf={resetTarget.idUser === user?.id}
              onReset={handleReset}
              light={lightMode}
              onClose={() => setResetTarget(null)}
            />
          </div>
        </div>
      ) : null}

      {showAddStaff ? (
        <div className={s.modalOverlay} onClick={() => setShowAddStaff(false)}>
          <div className={s.modalBody} onClick={(e) => e.stopPropagation()}>
            <AddStaffForm
              onCreate={handleCreate}
              light={lightMode}
              onClose={() => setShowAddStaff(false)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
